import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import API from '@/config/api';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  DollarSign, TrendingUp, TrendingDown, Receipt, Plus, Pencil, Trash2,
  Download, BarChart3, PieChart, ArrowUpRight, ArrowDownRight, Loader2,
  FileText, Calendar, Filter, RefreshCw, Minus, Printer, Settings, Ban
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart as RPieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#D4A853', '#2563eb', '#16a34a', '#dc2626', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899', '#84cc16', '#6366f1', '#f97316', '#14b8a6', '#e11d48', '#a855f7', '#0ea5e9'];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const AccountingPage = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');

  return (
    <DashboardLayout title="Accounting">
      <div className="space-y-6" data-testid="accounting-page">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-muted/50 p-1 rounded-xl">
            <TabsTrigger value="dashboard" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <BarChart3 className="w-4 h-4 mr-2" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="expenses" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Receipt className="w-4 h-4 mr-2" /> Expenses
            </TabsTrigger>
            <TabsTrigger value="income" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <DollarSign className="w-4 h-4 mr-2" /> Income
            </TabsTrigger>
            <TabsTrigger value="pnl" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <FileText className="w-4 h-4 mr-2" /> Profit & Loss
            </TabsTrigger>
            <TabsTrigger value="checks" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Printer className="w-4 h-4 mr-2" /> Checks
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard"><FinancialDashboard getAuthHeaders={getAuthHeaders} /></TabsContent>
          <TabsContent value="expenses"><ExpenseTracker getAuthHeaders={getAuthHeaders} /></TabsContent>
          <TabsContent value="income"><IncomeTracker getAuthHeaders={getAuthHeaders} /></TabsContent>
          <TabsContent value="pnl"><ProfitLoss getAuthHeaders={getAuthHeaders} /></TabsContent>
          <TabsContent value="checks"><CheckPrinting getAuthHeaders={getAuthHeaders} /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

// ==================== FINANCIAL DASHBOARD ====================

const FinancialDashboard = ({ getAuthHeaders }) => {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState([]);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, trendRes] = await Promise.all([
        axios.get(`${API}/accounting/dashboard?period=${period}`, getAuthHeaders()),
        axios.get(`${API}/accounting/trends?months=6`, getAuthHeaders()),
      ]);
      setData(dashRes.data);
      setTrends(trendRes.data);
    } catch (err) {
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [period]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!data) return null;

  const cards = [
    { title: 'Revenue', value: data.revenue.current, prev: data.revenue.previous, change: data.revenue.change, icon: <TrendingUp className="w-5 h-5" />, color: 'bg-green-100 text-green-700', sub: `${data.revenue.income_count} entries${data.revenue.cacfp > 0 ? ` | CACFP: $${data.revenue.cacfp.toLocaleString()}` : ''}` },
    { title: 'Expenses', value: data.expenses.current, prev: data.expenses.previous, change: data.expenses.change, icon: <TrendingDown className="w-5 h-5" />, color: 'bg-red-100 text-red-700', sub: `${data.expenses.breakdown.length} categories`, invertChange: true },
    { title: 'Net Profit', value: data.net_profit.current, prev: data.net_profit.previous, change: data.net_profit.change, icon: <DollarSign className="w-5 h-5" />, color: data.net_profit.current >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700', sub: 'Revenue - Expenses' },
    { title: 'Outstanding', value: data.outstanding.amount, icon: <Receipt className="w-5 h-5" />, color: 'bg-amber-100 text-amber-700', sub: `${data.outstanding.count} unpaid invoices` },
  ];

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {['month', 'quarter', 'year'].map(p => (
            <Button key={p} size="sm" variant={period === p ? 'default' : 'outline'} onClick={() => setPeriod(p)} data-testid={`period-${p}`}>
              {p === 'month' ? 'This Month' : p === 'quarter' ? 'This Quarter' : 'This Year'}
            </Button>
          ))}
        </div>
        <Button size="sm" variant="ghost" onClick={fetchData}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <Card key={i} className="card-clay" data-testid={`finance-card-${c.title.toLowerCase().replace(' ','-')}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-muted-foreground">{c.title}</span>
                <div className={`w-10 h-10 rounded-xl ${c.color} flex items-center justify-center`}>{c.icon}</div>
              </div>
              <p className={`text-2xl font-bold ${c.value < 0 ? 'text-red-600' : ''}`}>
                {c.value < 0 ? '-' : ''}${Math.abs(c.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-muted-foreground">{c.sub}</span>
                {c.change !== undefined && (
                  <Badge variant="outline" className={`text-xs ${(c.invertChange ? c.change <= 0 : c.change >= 0) ? 'text-green-600 border-green-200 bg-green-50' : 'text-red-600 border-red-200 bg-red-50'}`}>
                    {c.change >= 0 ? <ArrowUpRight className="w-3 h-3 mr-0.5" /> : <ArrowDownRight className="w-3 h-3 mr-0.5" />}
                    {Math.abs(c.change)}%
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Trend Chart */}
        <Card className="card-clay lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Revenue vs Expenses (6 Months)</CardTitle></CardHeader>
          <CardContent>
            {trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={trends} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                  <Tooltip formatter={(val) => [`$${Number(val).toLocaleString('en-US', {minimumFractionDigits:2})}`, '']} />
                  <Legend />
                  <Bar dataKey="revenue" name="Revenue" fill="#16a34a" radius={[4,4,0,0]} />
                  <Bar dataKey="expenses" name="Expenses" fill="#dc2626" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">No trend data yet</div>
            )}
          </CardContent>
        </Card>

        {/* Expense Breakdown Pie */}
        <Card className="card-clay">
          <CardHeader><CardTitle className="text-base">Expense Breakdown</CardTitle></CardHeader>
          <CardContent>
            {data.expenses.breakdown.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <RPieChart>
                    <Pie data={data.expenses.breakdown} dataKey="amount" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40}>
                      {data.expenses.breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(val) => `$${Number(val).toLocaleString('en-US', {minimumFractionDigits:2})}`} />
                  </RPieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-2">
                  {data.expenses.breakdown.slice(0, 5).map((item, i) => (
                    <div key={item.category} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="text-muted-foreground">{item.name}</span>
                      </div>
                      <span className="font-medium">${item.amount.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">No expenses recorded</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== EXPENSE TRACKER ====================

const ExpenseTracker = ({ getAuthHeaders }) => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const now = new Date();
  const [filterMonth, setFilterMonth] = useState(String(now.getMonth() + 1));
  const [filterYear, setFilterYear] = useState(String(now.getFullYear()));

  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    vendor: '',
    description: '',
    amount: '',
    is_recurring: false,
    recurring_frequency: '',
    notes: '',
    tax_deductible: true,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMonth !== 'all') params.append('month', filterMonth);
      if (filterYear) params.append('year', filterYear);
      if (filterCategory !== 'all') params.append('category', filterCategory);

      const [expRes, catRes] = await Promise.all([
        axios.get(`${API}/accounting/expenses?${params}`, getAuthHeaders()),
        axios.get(`${API}/accounting/categories`, getAuthHeaders()),
      ]);
      setExpenses(expRes.data);
      setCategories(catRes.data.expense || catRes.data);
    } catch {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filterMonth, filterYear, filterCategory]);

  const totalExpenses = useMemo(() => expenses.reduce((s, e) => s + e.amount, 0), [expenses]);

  const resetForm = () => {
    setForm({ date: new Date().toISOString().split('T')[0], category: '', vendor: '', description: '', amount: '', is_recurring: false, recurring_frequency: '', notes: '', tax_deductible: true });
    setEditingExpense(null);
  };

  const openNew = () => { resetForm(); setDialogOpen(true); };
  const openEdit = (exp) => {
    setForm({ ...exp, amount: String(exp.amount) });
    setEditingExpense(exp);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.category || !form.description || !form.amount || !form.date) {
      toast.error('Please fill in date, category, description, and amount');
      return;
    }
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (editingExpense) {
        await axios.put(`${API}/accounting/expenses/${editingExpense.id}`, payload, getAuthHeaders());
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/accounting/expenses`, payload, getAuthHeaders());
        toast.success('Expense added');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/accounting/expenses/${id}`, getAuthHeaders());
      toast.success('Expense deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const exportCSV = () => {
    const headers = ['Date', 'Category', 'Vendor', 'Description', 'Amount', 'Tax Deductible', 'Recurring'];
    const rows = expenses.map(e => [e.date, e.category, e.vendor, e.description, e.amount, e.tax_deductible ? 'Yes' : 'No', e.is_recurring ? e.recurring_frequency : 'No']);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expenses_${filterYear}_${filterMonth}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[160px]"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-expenses-btn"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
          <Button size="sm" onClick={openNew} data-testid="add-expense-btn"><Plus className="w-4 h-4 mr-1" /> Add Expense</Button>
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center gap-4 text-sm">
        <span className="text-muted-foreground">{expenses.length} expenses</span>
        <span className="font-semibold">Total: ${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
      </div>

      {/* Table */}
      <Card className="card-clay">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Receipt className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No expenses found</p>
              <p className="text-sm mt-1">Click "Add Expense" to start tracking</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="mobile-cards">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Tax Ded.</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.map(exp => {
                    const catName = categories.find(c => c.id === exp.category)?.name || exp.category;
                    return (
                      <TableRow key={exp.id} data-testid={`expense-row-${exp.id}`}>
                        <TableCell className="text-sm">{new Date(exp.date + 'T12:00:00').toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{catName}</Badge>
                          {exp.is_recurring && <Badge className="ml-1 text-[10px] bg-blue-100 text-blue-700 border-blue-200" variant="outline">Recurring</Badge>}
                        </TableCell>
                        <TableCell className="text-sm">{exp.vendor || '-'}</TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">{exp.description}</TableCell>
                        <TableCell className="text-right font-medium">${exp.amount.toFixed(2)}</TableCell>
                        <TableCell>{exp.tax_deductible ? <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700">Yes</Badge> : <span className="text-xs text-muted-foreground">No</span>}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(exp)}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(exp.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
            <DialogDescription>Record a business expense for your center</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="expense-date" />
              </div>
              <div>
                <Label>Amount *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="number" step="0.01" min="0" className="pl-8" placeholder="0.00" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="expense-amount" />
                </div>
              </div>
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="expense-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description *</Label>
              <Input placeholder="e.g., Monthly rent payment" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="expense-description" />
            </div>
            <div>
              <Label>Vendor</Label>
              <Input placeholder="e.g., Landlord LLC" value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} data-testid="expense-vendor" />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Checkbox id="tax_ded" checked={form.tax_deductible} onCheckedChange={v => setForm(f => ({ ...f, tax_deductible: v }))} />
                <Label htmlFor="tax_ded" className="text-sm cursor-pointer">Tax Deductible</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="recurring" checked={form.is_recurring} onCheckedChange={v => setForm(f => ({ ...f, is_recurring: v, recurring_frequency: v ? 'monthly' : '' }))} />
                <Label htmlFor="recurring" className="text-sm cursor-pointer">Recurring</Label>
              </div>
            </div>
            {form.is_recurring && (
              <Select value={form.recurring_frequency} onValueChange={v => setForm(f => ({ ...f, recurring_frequency: v }))}>
                <SelectTrigger><SelectValue placeholder="Frequency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            )}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea rows={2} placeholder="Additional details..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-expense-btn">{editingExpense ? 'Update' : 'Add Expense'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== PROFIT & LOSS ====================

const ProfitLoss = ({ getAuthHeaders }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const now = new Date();
  const [startDate, setStartDate] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
  const [endDate, setEndDate] = useState(now.toISOString().split('T')[0]);
  const [preset, setPreset] = useState('this_month');

  const applyPreset = (p) => {
    setPreset(p);
    const today = new Date();
    let s, e;
    if (p === 'this_month') {
      s = new Date(today.getFullYear(), today.getMonth(), 1);
      e = today;
    } else if (p === 'last_month') {
      s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      e = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (p === 'this_quarter') {
      const qm = Math.floor(today.getMonth() / 3) * 3;
      s = new Date(today.getFullYear(), qm, 1);
      e = today;
    } else if (p === 'this_year') {
      s = new Date(today.getFullYear(), 0, 1);
      e = today;
    } else if (p === 'last_year') {
      s = new Date(today.getFullYear() - 1, 0, 1);
      e = new Date(today.getFullYear() - 1, 11, 31);
    }
    setStartDate(s.toISOString().split('T')[0]);
    setEndDate(e.toISOString().split('T')[0]);
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/accounting/pnl?start_date=${startDate}&end_date=${endDate}`, getAuthHeaders());
      setReport(res.data);
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [startDate, endDate]);

  const exportCSV = () => {
    if (!report) return;
    const lines = [
      ['Profit & Loss Statement'],
      [`Period: ${startDate} to ${endDate}`],
      [],
      ['--- REVENUE ---'],
      ...(report.revenue.lines || []).map(l => [l.name, `(${l.count} entries)`, l.amount]),
      ['TOTAL REVENUE', '', report.revenue.total],
      [],
      ['--- EXPENSES ---'],
      ...report.expenses.lines.map(l => [l.name, `(${l.count} entries)`, l.amount]),
      ['TOTAL EXPENSES', '', report.expenses.total],
      [],
      ['NET INCOME', '', report.net_income],
      ['Profit Margin', '', `${report.profit_margin}%`],
      [],
      ['Tax Deductible Expenses', '', report.expenses.tax_deductible],
    ];
    const csv = lines.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pnl_${startDate}_${endDate}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card className="card-clay">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-wrap gap-1.5">
              {[
                { id: 'this_month', label: 'This Month' },
                { id: 'last_month', label: 'Last Month' },
                { id: 'this_quarter', label: 'Quarter' },
                { id: 'this_year', label: 'YTD' },
                { id: 'last_year', label: 'Last Year' },
              ].map(p => (
                <Button key={p.id} size="sm" variant={preset === p.id ? 'default' : 'outline'} onClick={() => applyPreset(p.id)} data-testid={`pnl-preset-${p.id}`}>
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-auto">
              <Input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPreset(''); }} className="w-[140px] sm:w-[150px] flex-1 sm:flex-none" />
              <Minus className="w-4 h-4 text-muted-foreground" />
              <Input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPreset(''); }} className="w-[140px] sm:w-[150px] flex-1 sm:flex-none" />
              <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-pnl-btn"><Download className="w-4 h-4 mr-1" /> Export</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : report ? (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Revenue Card */}
          <Card className="card-clay">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-green-700">
                <TrendingUp className="w-5 h-5" /> Revenue
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(report.revenue.lines || []).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">No income recorded for this period</div>
              ) : (
                report.revenue.lines.map(line => (
                  <div key={line.category} className="flex justify-between items-center py-2 border-b" data-testid={`pnl-revenue-${line.category}`}>
                    <span className="text-sm">{line.name}</span>
                    <div className="text-right">
                      <span className="font-medium">${line.amount.toLocaleString('en-US', {minimumFractionDigits:2})}</span>
                      <span className="text-xs text-muted-foreground ml-2">({line.count})</span>
                    </div>
                  </div>
                ))
              )}
              <div className="flex justify-between items-center py-3 bg-green-50 rounded-lg px-3 font-semibold">
                <span>Total Revenue</span>
                <span className="text-green-700">${report.revenue.total.toLocaleString('en-US', {minimumFractionDigits:2})}</span>
              </div>
            </CardContent>
          </Card>

          {/* Expenses Card */}
          <Card className="card-clay">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-red-700">
                <TrendingDown className="w-5 h-5" /> Expenses
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {report.expenses.lines.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">No expenses recorded for this period</div>
              ) : (
                report.expenses.lines.map(line => (
                  <div key={line.category} className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm">{line.name}</span>
                    <div className="text-right">
                      <span className="font-medium">${line.amount.toLocaleString('en-US', {minimumFractionDigits:2})}</span>
                      <span className="text-xs text-muted-foreground ml-2">({line.count})</span>
                    </div>
                  </div>
                ))
              )}
              <div className="flex justify-between items-center py-3 bg-red-50 rounded-lg px-3 font-semibold">
                <span>Total Expenses</span>
                <span className="text-red-700">${report.expenses.total.toLocaleString('en-US', {minimumFractionDigits:2})}</span>
              </div>
            </CardContent>
          </Card>

          {/* Net Income Summary */}
          <Card className={`card-clay lg:col-span-2 ${report.net_income >= 0 ? 'border-green-200' : 'border-red-200'}`}>
            <CardContent className="p-6">
              <div className="grid sm:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Net Income</p>
                  <p className={`text-3xl font-bold ${report.net_income >= 0 ? 'text-green-700' : 'text-red-700'}`} data-testid="pnl-net-income">
                    {report.net_income < 0 ? '-' : ''}${Math.abs(report.net_income).toLocaleString('en-US', {minimumFractionDigits:2})}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Profit Margin</p>
                  <p className={`text-3xl font-bold ${report.profit_margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {report.profit_margin}%
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Tax Deductible Expenses</p>
                  <p className="text-3xl font-bold text-blue-700">
                    ${report.expenses.tax_deductible.toLocaleString('en-US', {minimumFractionDigits:2})}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
};

// ==================== INCOME TRACKER (CACFP, Grants, etc.) ====================

const IncomeTracker = ({ getAuthHeaders }) => {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const now = new Date();
  const [filterMonth, setFilterMonth] = useState(String(now.getMonth() + 1));
  const [filterYear, setFilterYear] = useState(String(now.getFullYear()));

  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    category: 'cacfp',
    source: '',
    description: '',
    amount: '',
    is_recurring: false,
    recurring_frequency: '',
    notes: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMonth !== 'all') params.append('month', filterMonth);
      if (filterYear) params.append('year', filterYear);
      if (filterCategory !== 'all') params.append('category', filterCategory);

      const [incRes, catRes] = await Promise.all([
        axios.get(`${API}/accounting/income?${params}`, getAuthHeaders()),
        axios.get(`${API}/accounting/categories`, getAuthHeaders()),
      ]);
      setEntries(incRes.data);
      setCategories(catRes.data.income || []);
    } catch {
      toast.error('Failed to load income entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filterMonth, filterYear, filterCategory]);

  const totalIncome = useMemo(() => entries.reduce((s, e) => s + e.amount, 0), [entries]);

  const resetForm = () => {
    setForm({ date: new Date().toISOString().split('T')[0], category: 'cacfp', source: '', description: '', amount: '', is_recurring: false, recurring_frequency: '', notes: '' });
    setEditingEntry(null);
  };

  const openNew = () => { resetForm(); setDialogOpen(true); };
  const openEdit = (entry) => {
    setForm({ ...entry, amount: String(entry.amount) });
    setEditingEntry(entry);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.category || !form.description || !form.amount || !form.date) {
      toast.error('Please fill in date, category, description, and amount');
      return;
    }
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (editingEntry) {
        await axios.put(`${API}/accounting/income/${editingEntry.id}`, payload, getAuthHeaders());
        toast.success('Income entry updated');
      } else {
        await axios.post(`${API}/accounting/income`, payload, getAuthHeaders());
        toast.success('Income entry added');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this income entry?')) return;
    try {
      await axios.delete(`${API}/accounting/income/${id}`, getAuthHeaders());
      toast.success('Income entry deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const exportCSV = () => {
    const headers = ['Date', 'Category', 'Source', 'Description', 'Amount', 'Recurring'];
    const rows = entries.map(e => [e.date, e.category, e.source, e.description, e.amount, e.is_recurring ? e.recurring_frequency : 'No']);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `income_${filterYear}_${filterMonth}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        Track non-invoice revenue here: CACFP reimbursements, WI Shares subsidies, grants, fundraising, and donations. Invoice payments (tuition) are tracked automatically from Billing.
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Sources" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-income-btn"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
          <Button size="sm" onClick={openNew} data-testid="add-income-btn"><Plus className="w-4 h-4 mr-1" /> Add Income</Button>
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center gap-4 text-sm">
        <span className="text-muted-foreground">{entries.length} entries</span>
        <span className="font-semibold text-green-700">Total: ${totalIncome.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
      </div>

      {/* Table */}
      <Card className="card-clay">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : entries.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No income entries found</p>
              <p className="text-sm mt-1">Click "Add Income" to log CACFP reimbursements, grants, etc.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="mobile-cards">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map(entry => {
                    const catName = categories.find(c => c.id === entry.category)?.name || entry.category;
                    return (
                      <TableRow key={entry.id} data-testid={`income-row-${entry.id}`}>
                        <TableCell className="text-sm">{new Date(entry.date + 'T12:00:00').toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs ${entry.category === 'cacfp' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''}`}>{catName}</Badge>
                          {entry.is_recurring && <Badge className="ml-1 text-[10px] bg-blue-100 text-blue-700 border-blue-200" variant="outline">Recurring</Badge>}
                        </TableCell>
                        <TableCell className="text-sm">{entry.source || '-'}</TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">{entry.description}</TableCell>
                        <TableCell className="text-right font-medium text-green-700">${entry.amount.toFixed(2)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(entry)}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(entry.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Income Entry' : 'Add Income'}</DialogTitle>
            <DialogDescription>Log CACFP reimbursements, grants, subsidies, or other non-invoice income</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="income-date" />
              </div>
              <div>
                <Label>Amount *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="number" step="0.01" min="0" className="pl-8" placeholder="0.00" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="income-amount" />
                </div>
              </div>
            </div>
            <div>
              <Label>Income Type *</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="income-category"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description *</Label>
              <Input placeholder={form.category === 'cacfp' ? 'e.g., March 2026 CACFP reimbursement' : 'Describe the income'} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="income-description" />
            </div>
            <div>
              <Label>Source</Label>
              <Input placeholder={form.category === 'cacfp' ? 'e.g., USDA / DPI' : form.category === 'wi_shares' ? 'e.g., WI DCF' : 'e.g., Organization name'} value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} data-testid="income-source" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="inc_recurring" checked={form.is_recurring} onCheckedChange={v => setForm(f => ({ ...f, is_recurring: v, recurring_frequency: v ? 'monthly' : '' }))} />
              <Label htmlFor="inc_recurring" className="text-sm cursor-pointer">Recurring payment</Label>
            </div>
            {form.is_recurring && (
              <Select value={form.recurring_frequency} onValueChange={v => setForm(f => ({ ...f, recurring_frequency: v }))}>
                <SelectTrigger><SelectValue placeholder="Frequency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            )}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea rows={2} placeholder="Additional details..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-income-btn">{editingEntry ? 'Update' : 'Add Income'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== CHECK PRINTING ====================

const CheckPrinting = ({ getAuthHeaders }) => {
  const [checks, setChecks] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [writeCheckOpen, setWriteCheckOpen] = useState(false);
  const [printPreview, setPrintPreview] = useState(null);
  const [fromExpense, setFromExpense] = useState(false);
  const now = new Date();
  const [filterMonth, setFilterMonth] = useState(String(now.getMonth() + 1));
  const [filterYear, setFilterYear] = useState(String(now.getFullYear()));

  const [settingsForm, setSettingsForm] = useState({
    bank_name: '', routing_number: '', account_number: '',
    next_check_number: 1001, center_name: '', center_address: '', center_city_state_zip: ''
  });

  const [checkForm, setCheckForm] = useState({
    payee: '', amount: '', date: new Date().toISOString().split('T')[0], memo: '', expense_id: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMonth !== 'all') params.append('month', filterMonth);
      if (filterYear) params.append('year', filterYear);

      const [chkRes, setRes, expRes] = await Promise.all([
        axios.get(`${API}/accounting/checks?${params}`, getAuthHeaders()),
        axios.get(`${API}/accounting/checks/settings`, getAuthHeaders()),
        axios.get(`${API}/accounting/expenses?year=${filterYear}`, getAuthHeaders()),
      ]);
      setChecks(chkRes.data);
      setSettings(setRes.data);
      setSettingsForm(setRes.data);
      setExpenses(expRes.data);
    } catch {
      toast.error('Failed to load check data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filterMonth, filterYear]);

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/accounting/checks/settings`, settingsForm, getAuthHeaders());
      toast.success('Bank settings saved');
      setSettingsOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    }
  };

  const openWriteCheck = (expense = null) => {
    if (expense) {
      setCheckForm({
        payee: expense.vendor || '',
        amount: String(expense.amount),
        date: new Date().toISOString().split('T')[0],
        memo: expense.description || '',
        expense_id: expense.id,
      });
      setFromExpense(true);
    } else {
      setCheckForm({ payee: '', amount: '', date: new Date().toISOString().split('T')[0], memo: '', expense_id: '' });
      setFromExpense(false);
    }
    setWriteCheckOpen(true);
  };

  const createCheck = async () => {
    if (!checkForm.payee || !checkForm.amount) {
      toast.error('Payee and amount are required');
      return;
    }
    if (!settings?.bank_name) {
      toast.error('Please configure bank settings first');
      setSettingsOpen(true);
      return;
    }
    try {
      const res = await axios.post(`${API}/accounting/checks`, {
        ...checkForm, amount: parseFloat(checkForm.amount)
      }, getAuthHeaders());
      toast.success(`Check #${res.data.check_number} created`);
      setWriteCheckOpen(false);
      setPrintPreview([res.data]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create check');
    }
  };

  const voidCheck = async (id) => {
    if (!window.confirm('Void this check? This cannot be undone.')) return;
    try {
      await axios.put(`${API}/accounting/checks/${id}/void`, {}, getAuthHeaders());
      toast.success('Check voided');
      fetchData();
    } catch {
      toast.error('Failed to void check');
    }
  };

  const printChecks = (checksToprint) => {
    setPrintPreview(checksToprint);
    setTimeout(() => window.print(), 300);
  };

  const bankConfigured = settings?.bank_name && settings?.routing_number && settings?.account_number;

  return (
    <div className="space-y-4">
      {/* Print stylesheet */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .check-print-area, .check-print-area * { visibility: visible !important; }
          .check-print-area { position: fixed; top: 0; left: 0; width: 100%; z-index: 99999; background: white; }
          .check-single { page-break-inside: avoid; height: 3.5in; width: 8.5in; box-sizing: border-box; }
          @page { margin: 0; size: letter; }
        }
      `}</style>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setSettingsForm(settings || settingsForm); setSettingsOpen(true); }} data-testid="check-settings-btn">
            <Settings className="w-4 h-4 mr-1" /> Bank Settings
          </Button>
          <Button size="sm" onClick={() => openWriteCheck()} data-testid="write-check-btn">
            <Plus className="w-4 h-4 mr-1" /> Write Check
          </Button>
        </div>
      </div>

      {/* Bank config warning */}
      {!bankConfigured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          Configure your bank details (name, routing number, account number) before printing checks.
          <Button variant="link" className="text-amber-800 underline p-0 ml-1 h-auto" onClick={() => setSettingsOpen(true)}>Set up now</Button>
        </div>
      )}

      {/* From Expense quick actions */}
      {expenses.length > 0 && (
        <Card className="card-clay">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Quick: Write Check from Expense</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {expenses.filter(e => e.vendor).slice(0, 8).map(exp => (
                <Button key={exp.id} variant="outline" size="sm" className="text-xs" onClick={() => openWriteCheck(exp)}>
                  {exp.vendor} — ${exp.amount.toFixed(2)}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Checks History */}
      <Card className="card-clay">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : checks.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Printer className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No checks written yet</p>
              <p className="text-sm mt-1">Click "Write Check" to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="mobile-cards">
                <TableHeader>
                  <TableRow>
                    <TableHead>Check #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Payee</TableHead>
                    <TableHead>Memo</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checks.map(chk => (
                    <TableRow key={chk.id} data-testid={`check-row-${chk.id}`} className={chk.status === 'voided' ? 'opacity-50' : ''}>
                      <TableCell className="font-mono font-medium">{chk.check_number}</TableCell>
                      <TableCell className="text-sm">{new Date(chk.date + 'T12:00:00').toLocaleDateString()}</TableCell>
                      <TableCell className="text-sm font-medium">{chk.payee}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{chk.memo || '-'}</TableCell>
                      <TableCell className="text-right font-medium">${chk.amount.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${chk.status === 'voided' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                          {chk.status === 'voided' ? 'Voided' : 'Printed'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => printChecks([chk])} disabled={chk.status === 'voided'}>
                            <Printer className="w-3.5 h-3.5" />
                          </Button>
                          {chk.status !== 'voided' && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => voidCheck(chk.id)}>
                              <Ban className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Print Preview Area (hidden until print) */}
      {printPreview && (
        <div className="check-print-area">
          {printPreview.map((chk, idx) => (
            <div key={idx} className="check-single border-b border-dashed border-gray-300 p-8 relative" style={{ fontFamily: "'Courier New', monospace" }}>
              {/* Check header */}
              <div className="flex justify-between mb-6">
                <div>
                  <p className="font-bold text-lg">{chk.center_name}</p>
                  <p className="text-sm">{chk.center_address}</p>
                  <p className="text-sm">{chk.center_city_state_zip}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold">#{chk.check_number}</p>
                  <p className="text-sm mt-1">{new Date(chk.date + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
              </div>

              {/* Pay to */}
              <div className="mb-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-bold whitespace-nowrap">PAY TO THE ORDER OF</span>
                  <div className="flex-1 border-b-2 border-black pb-1">
                    <span className="text-base font-bold">{chk.payee}</span>
                  </div>
                  <div className="border-2 border-black px-3 py-1 min-w-[120px] text-right">
                    <span className="font-bold text-lg">${chk.amount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Amount in words */}
              <div className="mb-4">
                <div className="flex items-baseline gap-2">
                  <div className="flex-1 border-b-2 border-black pb-1">
                    <span className="text-sm">{chk.amount_words}</span>
                    <span className="text-sm ml-2">{'*'.repeat(Math.max(0, 60 - (chk.amount_words?.length || 0)))}</span>
                  </div>
                  <span className="text-xs font-bold">DOLLARS</span>
                </div>
              </div>

              {/* Memo and signature */}
              <div className="flex justify-between items-end mt-8">
                <div>
                  <span className="text-xs font-bold">MEMO: </span>
                  <span className="text-sm">{chk.memo || ''}</span>
                  <div className="border-b border-gray-400 w-48 mt-1"></div>
                </div>
                <div>
                  <div className="border-b-2 border-black w-56 mb-1"></div>
                  <p className="text-xs text-center">AUTHORIZED SIGNATURE</p>
                </div>
              </div>

              {/* Bank info line */}
              <div className="mt-6 pt-3 border-t border-gray-300 flex justify-between text-xs text-gray-600">
                <span>{chk.bank_name}</span>
                <span>Routing: {chk.routing_number} | Account: {'*'.repeat(Math.max(0, (chk.account_number?.length || 4) - 4))}{chk.account_number?.slice(-4)}</span>
              </div>
            </div>
          ))}
          <div className="p-4 text-center no-print">
            <Button onClick={() => { window.print(); }} className="mr-2"><Printer className="w-4 h-4 mr-1" /> Print</Button>
            <Button variant="outline" onClick={() => setPrintPreview(null)}>Close Preview</Button>
          </div>
        </div>
      )}

      {/* Bank Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Bank & Check Settings</DialogTitle>
            <DialogDescription>Configure your bank details for check printing</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Center / Business Name *</Label>
              <Input value={settingsForm.center_name} onChange={e => setSettingsForm(f => ({ ...f, center_name: e.target.value }))} placeholder="CareKids Child Care Center" data-testid="check-center-name" />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={settingsForm.center_address} onChange={e => setSettingsForm(f => ({ ...f, center_address: e.target.value }))} placeholder="123 Main Street" data-testid="check-center-address" />
            </div>
            <div>
              <Label>City, State, ZIP</Label>
              <Input value={settingsForm.center_city_state_zip} onChange={e => setSettingsForm(f => ({ ...f, center_city_state_zip: e.target.value }))} placeholder="Milwaukee, WI 53202" data-testid="check-city-state" />
            </div>
            <hr />
            <div>
              <Label>Bank Name *</Label>
              <Input value={settingsForm.bank_name} onChange={e => setSettingsForm(f => ({ ...f, bank_name: e.target.value }))} placeholder="e.g., Associated Bank" data-testid="check-bank-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Routing Number *</Label>
                <Input value={settingsForm.routing_number} onChange={e => setSettingsForm(f => ({ ...f, routing_number: e.target.value }))} placeholder="9 digits" maxLength={9} data-testid="check-routing" />
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input value={settingsForm.account_number} onChange={e => setSettingsForm(f => ({ ...f, account_number: e.target.value }))} placeholder="Account number" data-testid="check-account" />
              </div>
            </div>
            <div>
              <Label>Starting Check Number</Label>
              <Input type="number" value={settingsForm.next_check_number} onChange={e => setSettingsForm(f => ({ ...f, next_check_number: parseInt(e.target.value) || 1001 }))} data-testid="check-starting-num" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={saveSettings} data-testid="save-check-settings-btn">Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Write Check Dialog */}
      <Dialog open={writeCheckOpen} onOpenChange={setWriteCheckOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{fromExpense ? 'Write Check from Expense' : 'Write Check'}</DialogTitle>
            <DialogDescription>
              {bankConfigured ? `Next check number: #${settings?.next_check_number || 1001}` : 'Configure bank settings first'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!fromExpense && expenses.length > 0 && (
              <div>
                <Label>Auto-fill from Expense (optional)</Label>
                <Select value={checkForm.expense_id || 'manual'} onValueChange={v => {
                  if (v === 'manual') {
                    setCheckForm(f => ({ ...f, expense_id: '', payee: '', amount: '', memo: '' }));
                  } else {
                    const exp = expenses.find(e => e.id === v);
                    if (exp) setCheckForm(f => ({ ...f, expense_id: exp.id, payee: exp.vendor || '', amount: String(exp.amount), memo: exp.description || '' }));
                  }
                }}>
                  <SelectTrigger><SelectValue placeholder="Manual entry" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual entry</SelectItem>
                    {expenses.filter(e => e.vendor).map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.vendor} — ${e.amount.toFixed(2)} ({e.description})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Pay to the Order of *</Label>
              <Input value={checkForm.payee} onChange={e => setCheckForm(f => ({ ...f, payee: e.target.value }))} placeholder="Payee / Vendor name" data-testid="check-payee" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="number" step="0.01" min="0" className="pl-8" value={checkForm.amount} onChange={e => setCheckForm(f => ({ ...f, amount: e.target.value }))} data-testid="check-amount" />
                </div>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={checkForm.date} onChange={e => setCheckForm(f => ({ ...f, date: e.target.value }))} data-testid="check-date" />
              </div>
            </div>
            <div>
              <Label>Memo</Label>
              <Input value={checkForm.memo} onChange={e => setCheckForm(f => ({ ...f, memo: e.target.value }))} placeholder="e.g., March rent payment" data-testid="check-memo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWriteCheckOpen(false)}>Cancel</Button>
            <Button onClick={createCheck} data-testid="create-check-btn" disabled={!bankConfigured}>
              <Printer className="w-4 h-4 mr-1" /> Create & Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountingPage;
